import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Boxes, QrCode } from 'lucide-react';
import { listAssets } from '../../api/assets';
import { printAssetLabel } from '../../lib/assetLabel';
import { useToast } from '../../components/ui/Toast';
import { PageHeader } from '../../components/ui/PageHeader';
import { Card } from '../../components/ui/Card';
import { Table, Thead, Th, Tr, Td } from '../../components/ui/Table';
import { StatusBadge } from '../../components/ui/Badge';
import { Select } from '../../components/ui/Select';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { EmptyState } from '../../components/ui/EmptyState';
import type { Asset, AssetStatus } from '../../types/api';

const STATUSES: AssetStatus[] = ['IN_SERVICE', 'SPARE', 'LOANED', 'FAULTY', 'RETURNED', 'DECOMMISSIONED'];

function money(value: string | number) {
  return `£${Number(value).toFixed(2)}`;
}

export function AssetsListPage() {
  const [status, setStatus] = useState<'' | AssetStatus>('');
  const [printingId, setPrintingId] = useState<string | null>(null);
  const { showToast } = useToast();

  const assetsQuery = useQuery({
    queryKey: ['assets', status],
    queryFn: () => listAssets(status ? { status } : {}),
  });

  async function handlePrintLabel(asset: Asset) {
    setPrintingId(asset.id);
    try {
      await printAssetLabel(asset);
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Could not print the label.', 'error');
    } finally {
      setPrintingId(null);
    }
  }

  return (
    <div>
      <PageHeader
        title="Asset Register"
        description="Every piece of equipment currently tracked, across all customers you can see."
        actions={
          <Select value={status} onChange={(e) => setStatus(e.target.value as '' | AssetStatus)} className="w-44">
            <option value="">All statuses</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s.replace('_', ' ')}
              </option>
            ))}
          </Select>
        }
      />

      <Card>
        {assetsQuery.isLoading ? (
          <div className="py-14 text-center">
            <Spinner />
          </div>
        ) : !assetsQuery.data || assetsQuery.data.length === 0 ? (
          <EmptyState icon={Boxes} title="No assets found" description="Delivered equipment will show up here." />
        ) : (
          <Table>
            <Thead>
              <tr>
                <Th>Item</Th>
                <Th>Serial</Th>
                <Th>Site</Th>
                <Th>Assigned to</Th>
                <Th>Status</Th>
                <Th>Condition</Th>
                <Th>Purchase date</Th>
                <Th>Purchase price</Th>
                <Th>Term</Th>
                <Th />
              </tr>
            </Thead>
            <tbody>
              {assetsQuery.data.map((asset) => (
                <Tr key={asset.id}>
                  <Td className="font-medium text-slate-900">
                    <Link to={`/assets/${asset.id}`} className="hover:text-brand-700">
                      {asset.orderLine?.customerCatalogItem?.name ?? 'Item'}
                    </Link>
                  </Td>
                  <Td>{asset.serialNumber ?? <span className="text-slate-400">-</span>}</Td>
                  <Td>{asset.currentSite?.name ?? '-'}</Td>
                  <Td>
                    {asset.currentUser
                      ? [asset.currentUser.firstName, asset.currentUser.lastName].filter(Boolean).join(' ') ||
                        asset.currentUser.email
                      : <span className="text-slate-400">Unassigned</span>}
                  </Td>
                  <Td>
                    <StatusBadge status={asset.status} kind="asset" />
                  </Td>
                  <Td>{asset.condition ?? <span className="text-slate-400">-</span>}</Td>
                  <Td>
                    {asset.purchaseDate ? new Date(asset.purchaseDate).toLocaleDateString() : <span className="text-slate-400">-</span>}
                  </Td>
                  <Td>{asset.purchasePrice ? money(asset.purchasePrice) : <span className="text-slate-400">-</span>}</Td>
                  <Td>{asset.termMonths ? `${asset.termMonths} mo` : <span className="text-slate-400">-</span>}</Td>
                  <Td className="text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      isLoading={printingId === asset.id}
                      onClick={() => handlePrintLabel(asset)}
                      aria-label={`Print label for ${asset.orderLine?.customerCatalogItem?.name ?? 'this asset'}`}
                    >
                      <QrCode className="h-3.5 w-3.5" />
                    </Button>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  );
}
